/**
 * AdminLayout.jsx — 공용 layout 메인 컴포넌트 (어셈블리)
 *
 * 모바일/데스크탑 사이드바, pull-to-refresh, 모바일 상단 바,
 * 외부회사 목록 조회를 담당합니다.
 * 사이드바 콘텐츠는 SidebarContent, 메뉴 아이템은 menuConfig에서 가져옵니다.
 *
 * 변경 이력 (2026-04-27, dashboard-layout-migration Step 2):
 *   - admin 전용 → admin/employee 공용 layout으로 인터페이스 일반화
 *   - userRole prop 신규 (기본값 'admin', Step 3에서 사이드바 메뉴 분기에 활용)
 *   - 라우트/메뉴/UI 동작은 동일 — 회귀 zero
 */
import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
// 모바일 헤더 바로가기 (2026-04-27): localStorage 기반 동적 렌더 — 하드코딩 (방문빈도/미팅지도/상황판) 제거
import { loadShortcuts, saveShortcuts } from './shortcutHelpers';
// 모바일 상단 바 + pull 인디케이터 (2026-08-25 분리 — 300줄 룰)
import { AdminMobileTopBar, PullRefreshIndicator } from './AdminMobileChrome';
import { cn } from '../../../lib/utils';
import { apiClient } from '../../../api/apiClient';
import { useDarkMode } from '../../../hooks/useDarkMode';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { useAppPermissions } from '../../../hooks/useAppPermissions';
import { SidebarContent } from './SidebarContent';
import { CompanyScopeProvider } from './CompanyScopeContext';
import { useIntegratedMenuList } from './useIntegratedMenuList';
import { ImpersonationBadge } from '../../dashboard/ImpersonationBadge';
import { PasswordChangeModalGlobal } from '../../../components/PasswordChangeModalGlobal';
// 모바일 pull-to-refresh (2026-09-28 분리 — 300줄 룰)
import { useAdminPullRefresh } from './useAdminPullRefresh';
import WidgetNoAccess from '../situationboard/WidgetNoAccess';
import { pendingRestore, clearRestoreMark, dropLastScreen } from '../../../utils/lastScreen';

function AdminLayout({ userRole = 'admin' }) {
  // userRole — 'admin' | 'employee'. Step 3에서 useAppPermissions와 함께 사이드바 메뉴 분기에 사용
  // permCodes Set 도 추출 — 옵션 3 (2026-04-27): 사이드바 권한 기반 메뉴 필터링용
  const { permissions, me, permCodes, loaded: permsLoaded } = useAppPermissions();
  const [pwManualOpen, setPwManualOpen] = useState(false); // 사이드바 푸터 비밀번호 변경 버튼용 (수동)
  const isImp = apiClient.isImpersonating();
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark, toggle: toggleDark } = useDarkMode();

  // ── 레이아웃 상태 ─────────────────────────────────────────────────────────
  const [mobileOpen,        setMobileOpen]        = useState(false);
  const [isMobile,          setIsMobile]          = useState(window.innerWidth <= 768);
  // 모바일 메뉴 드로어 열림 동안 배경(document) 스크롤 잠금 — 데스크탑은 조건이 항상 false (no-op)
  useBodyScrollLock(isMobile && mobileOpen);
  const {
    mainRef, pullDistance, pullRefreshing, isPullReady,
    onMainTouchStart, onMainTouchMove, onMainTouchEnd,
  } = useAdminPullRefresh({ isMobile, mobileOpen, pathname: location.pathname });
  // 모바일 헤더 바로가기 — DB user_preferences(key='mobile_shortcuts') 와 동기화 (디바이스 무관)
  const [mobileShortcuts,   setMobileShortcuts]   = useState([]);
  async function handleSaveShortcuts(paths) {
    setMobileShortcuts(paths); // optimistic update
    try { await saveShortcuts(paths); } catch (_) { /* 실패 시 toast 등 후속 */ }
  }
  // 동적 외부회사 목록: 라우트 변경 시마다 API에서 재조회
  const [externalCompanies, setExternalCompanies] = useState([]);
  // 통합 메뉴 (Phase 1-1c): 사용자가 접근 가능한 통합 메뉴 목록 — 라우트 변경마다 다시 받는다.
  //   작업 대상 "통합관리"가 이 목록으로 정해진다(2026-10-01): 실패 시 마지막 목록 유지 + refresh (useIntegratedMenuList.js)
  const integratedList = useIntegratedMenuList(location.pathname);

  // ── 외부회사 목록 조회 (라우트 변경마다) ─────────────────────────────────
  // (회사 아코디언은 2026-09-03 회사 선택창으로 대체 — CompanyScopeContext 가 이 목록을 소비)
  useEffect(() => {
    apiClient.get('/api/my/external-companies')
      .then((res) => {
        if (res.ok && res.data?.list) setExternalCompanies(res.data.list);
      })
      .catch(() => {}); // 권한 없거나 실패해도 메뉴 없이 조용히 처리
  }, [location.pathname]);

  // ── 모바일 헤더 바로가기 초기 로드 (마운트 1회) — DB user_preferences ─────
  useEffect(() => {
    let active = true;
    loadShortcuts().then((paths) => { if (active) setMobileShortcuts(paths); }).catch(() => {});
    return () => { active = false; };
  }, []);

  // ── 화면 너비 변경 감지 → 모바일 여부 갱신 ───────────────────────────────
  useEffect(() => {
    function onResize() {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) setMobileOpen(false); // 데스크탑으로 전환 시 모바일 메뉴 닫기
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── 마지막 작업 화면 문지기(2026-10-26) ── 기억한 화면으로 열었는데(utils/lastScreen) 그 주소가 왼쪽 메뉴에 안 보이면
  //   (그사이 열쇠가 빠짐 — 메뉴 줄이 보이면 MenuItem 이 표시를 지운다) 기억을 지우고 기본 화면(/ → HomeRedirect)으로.
  //   다른 화면으로 옮겨 갔으면 표시만 지운다(새 화면은 MenuItem 이 기억). 메뉴·작업 대상이 늦게 와도 4초 기다린다.
  useEffect(() => {
    const pending = pendingRestore();
    if (!pending) return undefined;
    if (pending !== location.pathname) { clearRestoreMark(); return undefined; }
    if (!permsLoaded) return undefined;
    const t = setTimeout(() => {
      if (pendingRestore() === location.pathname) { dropLastScreen(); navigate('/', { replace: true }); }
    }, 4000);
    return () => clearTimeout(t);
  }, [location.pathname, permsLoaded, navigate]);

  /** 로그아웃 처리 후 로그인 페이지로 이동 */
  async function logout() {
    await apiClient.logout();
    navigate('/login');
  }

  // ── 렌더 ──────────────────────────────────────────────────────────────────

  return (
    // 회사 선택창 컨텍스트 — 사이드바(선택창·메뉴)와 본문(홈 카드 등)이 함께 소비 (2026-09-03)
    <CompanyScopeProvider
      me={me} externalCompanies={externalCompanies} integratedList={integratedList} pathname={location.pathname}
      homeKey={Boolean(permCodes && permCodes.has('HOME'))}
      boardKey={Boolean(permCodes && permCodes.has('SITUATION_BOARD'))}
      callHqKey={Boolean(permCodes && permCodes.has('TM_CALL_MANAGEMENT_JEJU'))}
      /* 통합 메뉴 관리 열쇠(2026-10-15) — 열쇠 목록을 못 받았으면(불러오기 끝) 없음으로, 아직 모르면 null(문지기가 기다림) */
      menuManageKey={permCodes ? permCodes.has('INTEGRATED_MENU_MANAGEMENT') : (permsLoaded ? false : null)}
    >
    {/* 데스크탑: 자연 페이지 스크롤(html 사용) — main 자체 스크롤 제거로 input focus 시 자동 scrollIntoView 차단
        모바일: 기존 h-screen overflow-hidden 유지 (pull-to-refresh 동작 보존) */}
    <div className={cn(
      // 2026-08-26 디자인 통일: 전 페이지 흰 배경 (직원 관리 기준 확정 후 일괄 적용)
      'flex bg-[var(--bg-card)]',
      isMobile ? 'h-screen overflow-hidden' : 'min-h-screen',
    )}>

      {/* ── 모바일 상단 바 + pull-to-refresh 인디케이터 (분리: AdminMobileChrome.jsx) ── */}
      {isMobile && (
        <AdminMobileTopBar
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          me={me}
          mobileShortcuts={mobileShortcuts}
          location={location}
          navigate={navigate}
          permCodes={permCodes}
        />
      )}
      {isMobile && (
        <PullRefreshIndicator pullDistance={pullDistance} pullRefreshing={pullRefreshing} isPullReady={isPullReady} />
      )}

      {/* ── 모바일 메뉴 배경 오버레이 ── */}
      {isMobile && mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[998] touch-none"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* ── 사이드바 ──
          데스크탑: 고정 왼쪽 패널 (w-[220px])
          모바일:   화면 왼쪽에서 슬라이드인 (transform 애니메이션) */}
      <aside
        className={cn(
          // 2026-08-26 사용자 실험: 사이드바 흰색(라이트) — 테마 변수라 다크 모드에선 어두운 카드색 유지
          'bg-[var(--bg-card)] border-r border-[var(--border)] flex flex-col',
          isMobile
            ? [
                'fixed left-0 z-[999] top-[52px] bottom-0 w-60 overflow-y-auto overscroll-y-contain',
                'transition-transform duration-[220ms] ease-[cubic-bezier(0.4,0,0.2,1)]',
                mobileOpen ? 'translate-x-0 shadow-popup' : '-translate-x-full',
              ].join(' ')
            : 'w-[220px] flex-shrink-0 sticky top-0 h-screen overflow-y-auto overscroll-y-contain',
        )}
        style={isMobile ? { WebkitOverflowScrolling: 'touch' } : undefined}
      >
        <SidebarContent
          location={location}
          isMobile={isMobile}
          userRole={userRole}
          permissions={permissions}
          permCodes={permCodes}
          me={me}
          onPasswordChange={() => setPwManualOpen(true)}
          integratedMenus={integratedList.list}
          setMobileOpen={setMobileOpen}
          isDark={isDark}
          toggleDark={toggleDark}
          onLogout={logout}
          shortcuts={mobileShortcuts}
          onSaveShortcuts={handleSaveShortcuts}
        />
      </aside>

      {/* ── 메인 콘텐츠 영역 ── */}
      {/* 데스크탑: overflow-y-auto 제거 — input focus 시 main이 자동 scrollIntoView로 점프하는 것 차단
                   페이지 스크롤은 html 사용 (사이드바 sticky로 화면 따라옴)
          모바일: 기존 자체 스크롤 유지 (pull-to-refresh + 모바일 상단바 fixed) */}
      {/* Step 8 글로벌: 위장 접속 뱃지 + 비밀번호 변경 모달 (mustChangePassword 자동 + 사이드바 수동) */}
      {isImp && <ImpersonationBadge userName={me?.name} />}
      <PasswordChangeModalGlobal me={me} manualOpen={pwManualOpen} onClose={() => setPwManualOpen(false)} />
      <main
        ref={mainRef}
        className={cn(
          'flex-1 min-w-0 w-0 overflow-x-clip',
          // 데스크톱 위 여백 12px = 사이드바 프로필 박스(mt-3)와 같은 선 (2026-09-28, designStandard STD_HEADER_TOP 참고)
          // 휴대폰 (2026-09-28 사용자: "전체적으로 여백이 너무 타이트해 — 상하좌우 여백 좀 늘려줘", '보통' 선택):
          //   좌우 16px(전엔 0) · 위 72px = 상단바 52px + 20px(+머리글 4px = 제목이 바 아래 24px) · 아래 32px(전엔 16px).
          //   화면마다 따로 주던 좌우 여백은 이 값과 겹치지 않게 휴대폰에서 0 으로 맞췄다.
          isMobile ? 'px-4 pt-[72px] pb-8 overflow-y-auto' : 'px-5 pb-5 pt-3',
        )}
        onTouchStart={onMainTouchStart}
        onTouchMove={onMainTouchMove}
        onTouchEnd={onMainTouchEnd}
        onTouchCancel={onMainTouchEnd}
      >
        {/* drill-down (?as_user_id) 변화 시 자식 라우트 강제 재마운트 → useDashboardBase 등 hook 새 fetch (2026-04-29) */}
        <div key={`${location.pathname}|${new URLSearchParams(location.search).get('as_user_id') || 'self'}`}>
          {/* 상황판 위젯을 눌러 왔는데 그 메뉴를 못 여는 사람 → 메뉴 대신 "이 메뉴는 담당자가 쓰는 화면이에요"(2026-10-24, WidgetNoAccess) */}
          {location.state?.widgetNoAccess ? <WidgetNoAccess /> : <Outlet />}
        </div>
      </main>

    </div>
    </CompanyScopeProvider>
  );
}

export default AdminLayout;
